import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import { useClientAuth } from '../../auth/ClientAuthContext';
import API_BASE from '../../utils/api';
import ItemsTab from './tabs/ItemsTab';
import ItemStatusTab from './tabs/ItemStatusTab';
import ProposalsTab from './tabs/ProposalsTab';
import InvoicesTab from './tabs/InvoicesTab';
import RetainersTab from './tabs/RetainersTab';
import { uniqueRooms } from './tabs/badgeUtils';

const TABS = [
  { key: 'items', label: 'Items' },
  { key: 'proposals', label: 'Proposals' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'retainers', label: 'Retainers' },
  { key: 'itemStatus', label: 'Item Status' },
];

export default function ClientProjectView() {
  const { projectId } = useParams();
  const { clientAuth } = useClientAuth();
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tbInvoices, setTbInvoices] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [retainers, setRetainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('items');
  const [roomFilter, setRoomFilter] = useState('');

  const authHeaders = clientAuth?.token ? { Authorization: `Bearer ${clientAuth.token}` } : {};

  useEffect(() => {
    if (!clientAuth?.token || !projectId) return;

    const canViewInvoices = clientAuth.permissions?.viewInvoices !== false;
    const canViewProposals = clientAuth.permissions?.viewProposals !== false;
    const canViewRetainers = clientAuth.permissions?.viewRetainers !== false;

    Promise.all([
      fetch(`${API_BASE}/api/client/projects/${projectId}`, {
        credentials: 'include',
        headers: authHeaders,
      }).then((r) => (r.ok ? r.json() : null)),

      fetch(`${API_BASE}/api/client/projects/${projectId}/items`, {
        credentials: 'include',
        headers: authHeaders,
      }).then((r) => (r.ok ? r.json() : [])),

      canViewInvoices
        ? fetch(`${API_BASE}/api/client/projects/${projectId}/invoices`, {
            credentials: 'include',
            headers: authHeaders,
          }).then((r) => (r.ok ? r.json() : []))
        : Promise.resolve([]),

      canViewInvoices
        ? fetch(`${API_BASE}/api/client/projects/${projectId}/timebilling-invoices`, {
            credentials: 'include',
            headers: authHeaders,
          }).then((r) => (r.ok ? r.json() : []))
        : Promise.resolve([]),

      canViewProposals
        ? fetch(`${API_BASE}/api/client/projects/${projectId}/proposals`, {
            credentials: 'include',
            headers: authHeaders,
          }).then((r) => (r.ok ? r.json() : []))
        : Promise.resolve([]),

      fetch(`${API_BASE}/api/client/projects/${projectId}/purchase-orders`, {
        credentials: 'include',
        headers: authHeaders,
      }).then((r) => (r.ok ? r.json() : [])),

      canViewRetainers
        ? fetch(`${API_BASE}/api/client/projects/${projectId}/retainers`, {
            credentials: 'include',
            headers: authHeaders,
          }).then((r) => (r.ok ? r.json() : []))
        : Promise.resolve([]),
    ])
      .then(([proj, its, invs, tbInvs, props, pos, rets]) => {
        setProject(proj || null);
        setItems(its || []);
        setInvoices(invs || []);
        setTbInvoices(tbInvs || []);
        setProposals(props || []);
        setPurchaseOrders(pos || []);
        setRetainers(rets || []);
      })
      .catch((err) => setError(err.message || 'Failed to load project.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientAuth, projectId]);

  const rooms = useMemo(() => {
    if (activeTab === 'itemStatus') {
      const flat = purchaseOrders.flatMap((po) => (Array.isArray(po.items) ? po.items : []));
      return uniqueRooms(flat);
    }
    return uniqueRooms(items);
  }, [activeTab, items, purchaseOrders]);

  useEffect(() => {
    setRoomFilter('');
  }, [activeTab]);

  if (loading) return <p style={{ color: '#6b7280', fontSize: 14 }}>Loading…</p>;
  if (error) return <p className="cp-error">{error}</p>;

  const canApproveProposals = clientAuth.permissions?.approveProposals !== false;
  const showRoomFilter = (activeTab === 'items' || activeTab === 'itemStatus') && rooms.length > 0;

  return (
    <div className="cp-animate">
      <Link to="/client/dashboard" className="cp-back-link">← All projects</Link>

      {project?.pinterestBoardUrl && (
        <a
          href={project.pinterestBoardUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="cp-card cp-mood-board-link"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#E60023" aria-hidden="true">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
          </svg>
          <div>
            <div className="cp-mood-board-label">Mood Board</div>
            <div className="cp-mood-board-cta">View on Pinterest ↗</div>
          </div>
        </a>
      )}

      <div className="cp-tab-bar-row">
        <div className="cp-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`cp-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {showRoomFilter && (
          <div className="cp-filter-wrap">
            <SlidersHorizontal size={14} />
            Filter by
            <select
              className="cp-filter-select"
              value={roomFilter}
              onChange={(e) => setRoomFilter(e.target.value)}
            >
              <option value="">All Rooms</option>
              {rooms.map((room) => (
                <option key={room} value={room}>
                  {room}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {activeTab === 'items' && <ItemsTab items={items} projectId={projectId} roomFilter={roomFilter} />}
      {activeTab === 'proposals' && (
        <ProposalsTab proposals={proposals} projectId={projectId} canApprove={canApproveProposals} />
      )}
      {activeTab === 'invoices' && (
        <InvoicesTab invoices={invoices} tbInvoices={tbInvoices} projectId={projectId} />
      )}
      {activeTab === 'retainers' && (
        <RetainersTab retainers={retainers} projectId={projectId} />
      )}
      {activeTab === 'itemStatus' && (
        <ItemStatusTab
          purchaseOrders={purchaseOrders}
          items={items}
          projectId={projectId}
          roomFilter={roomFilter}
        />
      )}
    </div>
  );
}
